import React, { useLayoutEffect, useMemo, useRef } from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import { Box, Typography } from '@mui/material'

import { AppPillToggle } from '@components'
import { dsColorNew } from '@palette'
import { BAR_COLORS, METRICS, METRIC_OPTIONS, barsLabel, movementBars } from '../@services/movementBars'


const HEIGHT = 240

/** The amCharts columns: per ACRISS a bar of yours (coloured by sign) and one of your tier (grey); a click opens it. */
function BarsChart({ bars, metric, onDetail, label }) {
	const el = useRef(null)
	const detail = useRef(onDetail)
	detail.current = onDetail

	useLayoutEffect(() => {
		const root = am5.Root.new(el.current)
		root.setThemes([])   // the corporate chart theme (`Theme/charts.js`) is appended on every setThemes
		root._logo?.dispose()
		const chart = root.container.children.push(am5xy.XYChart.new(root, {
			panX: false, panY: false, wheelX: 'none', wheelY: 'none', paddingLeft: 0, paddingRight: 8, paddingTop: 8, paddingBottom: 0,
		}))
		const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20, cellStartLocation: 0.15, cellEndLocation: 0.85 })
		xRenderer.grid.template.set('forceHidden', true)
		const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'sipp', renderer: xRenderer }))
		const yRenderer = am5xy.AxisRendererY.new(root, {})
		yRenderer.labels.template.adapters.add('text', (text, target) => {
			const v = target.dataItem?.get('value')
			if (v === undefined || v === null) return text
			return metric === METRICS.EUR ? `${Math.round(v)} €` : `${Math.round(v * 10) / 10}%`
		})
		const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, extraMax: 0.08, extraMin: 0.08 }))
		// the 0 line reads stronger than the grid: above it went up, below it went down
		const zero = yAxis.createAxisRange(yAxis.makeDataItem({ value: 0 }))
		zero.get('grid').setAll({ stroke: am5.color(dsColorNew.textMuted), strokeOpacity: 0.6, forceHidden: false })

		const add = (field, text, name, fixed) => {
			const series = chart.series.push(am5xy.ColumnSeries.new(root, {
				name, xAxis, yAxis, valueYField: field, categoryXField: 'sipp',
				tooltip: am5.Tooltip.new(root, { labelText: `[bold]{categoryX}[/] · ${name}: {${text}}` }),
			}))
			series.columns.template.setAll({ width: am5.percent(88), strokeOpacity: 0, cursorOverStyle: 'pointer', tooltipY: 0, fill: am5.color(fixed || BAR_COLORS.flat) })
			if (!fixed) series.columns.template.adapters.add('fill', (fill, target) => (target.dataItem?.dataContext?.color ? am5.color(target.dataItem.dataContext.color) : fill))
			series.columns.template.events.on('click', ev => {
				const sipp = ev.target.dataItem?.dataContext?.sipp
				if (sipp) detail.current?.(sipp)
			})
			series.data.setAll(bars)
		}
		xAxis.data.setAll(bars)
		add('you', 'youText', 'You', null)
		add('tier', 'tierText', 'Your tier', BAR_COLORS.tier)
		return () => root.dispose()
	}, [bars, metric])

	return <Box ref={el} role="img" aria-label={label} sx={{ height: HEIGHT, width: '100%', minWidth: 0 }}/>
}

const swatch = color => ({ width: 12, height: 12, borderRadius: '3px', display: 'inline-block', backgroundColor: color })

/**
 * "Movement by ACRISS" bars (CU-869fn0gcp phase 2, brief block 6, criterion 18): above `MovementTable`, the same
 * figures — your change vs your tier's per ACRISS — as columns, with a `%` / `€` pill (€ in the page's unit). A
 * column opens the ACRISS detail (from the keyboard, the table's "Detail" does the same; the chart has an aria-label). Drawn with amCharts
 * itself, as `PriceCurve`: per-column colour by sign and the click need what the `AmchartSeeker` wrapper does not expose.
 */
export default function MovementCard({ data, view, metric, onMetric, onDetail }) {
	const bars = useMemo(() => movementBars(data, { metric, unit: view.unit }), [data, metric, view.unit])
	if (!bars.length) return null
	const lor = data?.sample?.lor || view.lor
	return <Box sx={{ mb: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: dsColorNew.textDark }}>
				<Box component="i" sx={swatch(BAR_COLORS.down)}/><Box component="i" sx={swatch(BAR_COLORS.up)}/>You <Box component="span" sx={{ color: dsColorNew.textMuted }}>(▼ green down · ▲ red up)</Box>
			</Box>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: dsColorNew.textDark }}>
				<Box component="i" sx={swatch(BAR_COLORS.tier)}/>Your tier
			</Box>
			<Box sx={{ flex: 1 }}/>
			<AppPillToggle aria-label="Bars in percent or euros" size="small" value={metric} options={METRIC_OPTIONS} onChange={onMetric}/>
		</Box>
		<BarsChart bars={bars} metric={metric} onDetail={onDetail} label={barsLabel(bars, metric)}/>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
			{metric === METRICS.EUR ? `Change in euros, ${view.unit === 'day' ? 'per day' : `total for ${lor} days`} (average of each rate's change)` : 'Change in %, the average of each rate\'s change, as the table below'} · click a column to open its ACRISS.
		</Typography>
	</Box>
}
